import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import QuickPreviewDrawer from "./QuickPreviewDrawer";
import StatusBadge from "./StatusBadge";
import SavedSearches, { useSavedSearches } from "./SavedSearches";
import SearchPeekCard from "./SearchPeekCard";
import { OS_DIRECTORY } from "@/constants/osNav";
import {
  Search, Command as CommandIcon, Plus, Pin, Clock, Compass,
  Building2, Package, FileText, ClipboardList, Truck, Barcode,
  Users, Store, Keyboard, Sparkles, ArrowRight, Sun,
} from "lucide-react";

const ASK_SUGGESTIONS = [
  "quotes pending this week",
  "quotes won this month",
  "orders delivered this month",
  "customer POs open",
];

const ASK_RX = /\b(quot|order|pos?\b|purchase)/i;
const fmtINR = (n) => n == null ? null :
  `₹ ${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const TYPE_ICON = {
  customer: Building2, product: Package, quote: FileText,
  customer_po: ClipboardList, order: Truck, serial: Barcode,
  vendor: Store, employee: Users, companies: Building2, contacts: Users,
  invoices: FileText, assets: Barcode, shipments: Truck, tickets: ClipboardList, locations: Building2,
};

const NAV_ACTIONS = [
  { key: "customers", label: "Go to Customers", to: "/customers", keys: "G C" },
  { key: "quotes", label: "Go to Quotations", to: "/commercial/crm/quotations", keys: "G Q" },
  { key: "orders", label: "Go to Orders", to: "/commercial/one-orders", keys: "G O" },
  { key: "products", label: "Go to Product Master", to: "/technology/product-master", keys: "G P" },
  { key: "pos", label: "Go to Customer POs", to: "/commercial/crm/customer-pos" },
  { key: "stock", label: "Go to Multi-Brand Stock", to: "/operations/multibrand-stock" },
  { key: "comms", label: "Go to Communications", to: "/admin/communications" },
  { key: "home", label: "Go to Home", to: "/", keys: "G D" },
];

const CREATE_ACTIONS = [
  { key: "new-quote", label: "Create Quotation", to: "/commercial/crm/quotations?new=1", ctx: ["quotation", "quote"] },
  { key: "new-customer", label: "Create Customer", to: "/commercial/crm/customers?new=1", ctx: ["customer", "companies"] },
  { key: "new-rfq", label: "Create RFQ / Enquiry", to: "/sales-desk/catalogue-enquiries?new=1", ctx: ["sales-desk", "enquir"] },
  { key: "new-po", label: "Create Customer PO", to: "/commercial/customer-po?new=1", ctx: ["customer-po"] },
  { key: "new-order", label: "Create Order (from PO)", to: "/commercial/one-orders?new=1", ctx: ["one-orders", "order"] },
  { key: "new-ticket", label: "Create Ticket", to: "/service-desk?new=1", ctx: ["service"] },
  { key: "new-pickup", label: "Create Pickup", to: "/pickups?new=1", ctx: ["pickup", "offboarding"] },
  { key: "new-task", label: "Create Task", to: "/work/projects?new=1", ctx: ["work"] },
  { key: "new-dc", label: "Create Delivery Challan", to: "/commercial/one-orders?dc=1", ctx: ["one-orders", "shipments"] },
  { key: "new-shipment", label: "Create Shipment", to: "/shipments?new=1", ctx: ["shipments"] },
];

const MODULE_ROUTES = OS_DIRECTORY.map((d) => ({ key: `${d.groupId}-${d.to}`, label: `${d.group} · ${d.label}`, to: d.to, group: d.group }));

const PREVIEWABLE = new Set(["customer", "quote", "customer_po", "order", "product", "serial", "employee"]);
const GO_MAP = { c: "/customers", q: "/commercial/crm/quotations", o: "/commercial/one-orders", p: "/technology/product-master", d: "/" };

const isTyping = () => {
  const el = document.activeElement;
  return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
};

export default function OneCommand({ testId }) {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [ask, setAsk] = useState(null);
  const [tookMs, setTookMs] = useState(null);
  const [fuzzy, setFuzzy] = useState(false);
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [pins, setPins] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [preview, setPreview] = useState(null);
  const [features, setFeatures] = useState([]);
  const [freq, setFreq] = useState([]);
  const goRef = useRef(0);
  const store = useSavedSearches(open);
  const inputRef = useRef(null);
  const refocus = useCallback(() => requestAnimationFrame(() => inputRef.current?.focus()), []);
  const navigate = useNavigate();
  const location = useLocation();

  // Global shortcuts: Cmd/Ctrl+K, "?" help, "g <key>" navigation
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === "Escape") { setOpen(false); return; }
      if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "?") { e.preventDefault(); setHelpOpen((h) => !h); return; }
      const now = Date.now();
      if (e.key.toLowerCase() === "g") { goRef.current = now; return; }
      if (goRef.current && now - goRef.current < 900) {
        const to = GO_MAP[e.key.toLowerCase()];
        goRef.current = 0;
        if (to) { e.preventDefault(); navigate(to); }
        return;
      }
      if (e.key.toLowerCase() === "q") { e.preventDefault(); navigate("/commercial/crm/quotations?new=1"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  // Load recent + pins when the palette opens
  useEffect(() => {
    if (!open) { setQ(""); setGroups([]); setTookMs(null); return; }
    api.get("/one-command/recent").then(({ data }) => setRecent(data.items || [])).catch(() => {});
    api.get("/one-command/pins").then(({ data }) => setPins(data.items || [])).catch(() => {});
    api.get("/one-command/pulse").then(({ data }) => setPulse(data)).catch(() => {});
    api.get("/navigator/frequent").then(({ data }) => setFreq(data.items || [])).catch(() => {});
  }, [open]);

  // Debounced universal search + Ask ONE
  useEffect(() => {
    const trimmed = q.trim();
    if (!open || trimmed.length < 2) { setGroups([]); setAsk(null); setTookMs(null); setFeatures([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      const isQuestion = trimmed.split(/\s+/).length >= 2 && ASK_RX.test(trimmed);
      try {
        const [searchRes, askRes] = await Promise.all([
          api.get("/global-search/", { params: { q: trimmed } }),
          isQuestion
            ? api.get("/one-command/ask", { params: { q: trimmed } }).catch(() => null)
            : Promise.resolve(null),
        ]);
        setGroups(searchRes.data.groups || []);
        setTookMs(searchRes.data.took_ms);
        setFuzzy(!!searchRes.data.fuzzy);
        setAsk(askRes?.data?.understood ? askRes.data : null);
        api.get("/navigator/search", { params: { q: trimmed } })
          .then(({ data }) => setFeatures(data.items || [])).catch(() => setFeatures([]));
        api.post("/one-command/perf", { metric: "global_search", ms: searchRes.data.took_ms || 0 }).catch(() => {});
      } catch { setGroups([]); setAsk(null); }
      finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const recordRecent = useCallback((item) => {
    api.post("/one-command/recent", {
      entity_type: item.type, entity_id: item.id,
      title: item.title, subtitle: item.subtitle || null, route: item.route || null,
    }).catch(() => {});
  }, []);

  const openPreview = useCallback((item) => {
    recordRecent(item);
    setOpen(false);
    setPreview(item);
  }, [recordRecent]);

  const goTo = useCallback((to) => { setOpen(false); navigate(to); }, [navigate]);

  const openFeature = useCallback((f) => {
    api.post("/navigator/track", { feature_id: f.id }).catch(() => {});
    setOpen(false); navigate(f.path);
  }, [navigate]);

  const createActions = useMemo(() => {
    const path = location.pathname;
    return [...CREATE_ACTIONS].sort((a, b) => {
      const am = a.ctx.some((c) => path.includes(c)) ? -1 : 0;
      const bm = b.ctx.some((c) => path.includes(c)) ? -1 : 0;
      return am - bm;
    });
  }, [location.pathname]);

  const hasQuery = q.trim().length >= 2;
  const itemByValue = useMemo(() => {
    const m = new Map();
    const add = (v, it) => { m.set(v, it); m.set(v.toLowerCase(), it); };
    groups.forEach((g) => g.items.forEach((it) => add(`${it.type}-${it.id}`, it)));
    (ask?.items || []).forEach((it) => add(`ask-${it.type}-${it.id}`, it));
    return m;
  }, [groups, ask]);
  const deviceItem = useMemo(() => ({ type: "device", id: q.trim(), title: q.trim(), subtitle: "Open the full device lifecycle for this serial", route: `/devices/${encodeURIComponent(q.trim())}` }), [q]);
  const exactHit = groups.some((g) => (g.items || []).some((i) => String(i.title || "").toUpperCase() === q.trim().toUpperCase()));
  const showDevice = hasQuery && /^[A-Za-z0-9-]{6,}$/.test(q.trim()) && !exactHit;
  const firstItem = groups[0]?.items?.[0] || null;
  const activeItem = !hasQuery ? null
    : active.toLowerCase().startsWith("device-") ? (showDevice ? deviceItem : firstItem)
    : itemByValue.get(active) || itemByValue.get(active.toLowerCase()) || null;
  const canPreview = (it) => PREVIEWABLE.has(it.type) && !(it.type === "employee" && it.route?.startsWith("/today"));
  const ql = q.trim().toLowerCase();
  const matchedCreate = useMemo(() => hasQuery ? CREATE_ACTIONS.filter((a) => a.label.toLowerCase().includes(ql.replace(/^(new|create|add)\s+/, ""))).slice(0, 5) : [], [hasQuery, ql]);
  const matchedModules = useMemo(() => hasQuery ? MODULE_ROUTES.filter((m) => m.label.toLowerCase().includes(ql.replace(/^(go to|open|goto)\s+/, ""))).slice(0, 6) : [], [hasQuery, ql]);

  return (
    <>
      <button type="button" data-testid={testId} onClick={() => setOpen(true)}
        aria-label="Search or create"
        className="w-full flex items-center justify-center sm:justify-start gap-2 h-9 px-0 sm:px-3 rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface-alt)] hover:bg-white hover:border-slate-300 transition-colors text-left">
        <Search className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.6} />
        <span className="hidden sm:block xl:hidden min-w-0 text-sm text-slate-500 truncate">Search…</span>
        <span className="hidden xl:block min-w-0 text-sm text-slate-500 truncate">Search customers, quotes, orders, serials, tickets… or create</span>
        <span className="ml-auto hidden lg:flex items-center gap-1 text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white font-mono">
          <CommandIcon className="h-3 w-3" strokeWidth={2} />K
        </span>
      </button>

      {open && (
        createPortal(
<div className="fixed inset-0 z-palette bg-slate-900/40 backdrop-blur-[2px] flex items-start justify-center pt-20 px-4"
          onClick={() => setOpen(false)} data-testid="one-command-overlay">
          <div className={`bg-white w-full max-w-2xl ${hasQuery ? "lg:max-w-5xl" : ""} rounded-lg shadow-2xl border border-slate-200 overflow-hidden transition-[max-width] duration-200`}
            onClick={(e) => e.stopPropagation()} data-testid="one-command-panel">
           <div className="flex">
            <div className="flex-1 min-w-0">
            <Command shouldFilter={!hasQuery} loop value={active} onValueChange={setActive}>
              <CommandInput ref={inputRef} autoFocus value={q} onValueChange={setQ}
                placeholder="Search customers, products, quotes, orders, serials… or type a command"
                data-testid="one-command-input" />
              {!hasQuery && <SavedSearches store={store} onPick={(v) => { setQ(v); refocus(); }} onDone={refocus} />}
              {!hasQuery && pulse && (
                <div className="px-3 pt-3 pb-1" data-testid="oc-pulse">
                  <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-slate-400 mb-1.5 flex items-center gap-1.5">
                    <Sun className="h-3 w-3 text-amber-500" /> Daily pulse · {pulse.date}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {pulse.stats.map((s) => (
                      <button key={s.key} type="button"
                        data-testid={`oc-pulse-${s.key}`}
                        onClick={() => setQ(s.ask)}
                        title={`Ask: ${s.ask}`}
                        className="rounded-md border border-slate-200 bg-slate-50 hover:bg-amber-50 hover:border-amber-200 px-2.5 py-2 text-left transition-colors">
                        <div className="text-lg font-semibold text-slate-900 leading-none">{s.count}</div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">{s.label}</div>
                        {s.total_value > 0 && (
                          <div className="text-[11px] font-mono text-slate-600 mt-0.5 truncate">{fmtINR(s.total_value)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <CommandList className={hasQuery ? "max-h-[55vh]" : "max-h-[38vh]"}>
                <CommandEmpty>
                  {loading ? "Searching…" : hasQuery ? (
                    <div className="space-y-2" data-testid="oc-no-results">
                      <div>No matches for “{q.trim()}”.</div>
                      <div className="flex flex-wrap gap-1.5 justify-center">
                        <button onClick={() => goTo("/commercial/crm/customers?new=1")}
                          className="text-[11px] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">+ New Customer</button>
                        <button onClick={() => goTo("/commercial/crm/quotations?new=1")}
                          className="text-[11px] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">+ New Quotation</button>
                        <button onClick={() => goTo("/technology/product-master")}
                          className="text-[11px] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">Browse Product Master</button>
                        <button onClick={() => goTo("/customers")}
                          className="text-[11px] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">Browse Customers</button>
                      </div>
                    </div>
                  ) : "Type to search, or pick an action."}
                </CommandEmpty>

                {showDevice && (
                  <CommandGroup heading="Device 360°" forceMount>
                    <CommandItem value={`device-${q}`} data-testid="oc-device-360" onSelect={() => goTo(`/devices/${encodeURIComponent(q.trim())}`)}>
                      <Compass className="h-4 w-4 text-violet-600" />
                      <span className="flex-1">Open Device 360° for <span className="font-mono">{q.trim()}</span></span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {hasQuery && matchedCreate.length > 0 && (
                  <CommandGroup heading="Create" forceMount>
                    {matchedCreate.map((a) => (
                      <CommandItem key={`mc-${a.key}`} value={`create-${a.label}`} data-testid={`oc-create-${a.key}`}
                        onSelect={() => goTo(a.to)}>
                        <Plus className="h-4 w-4 text-emerald-600" />
                        <span className="flex-1">{a.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {hasQuery && matchedModules.length > 0 && (
                  <CommandGroup heading="Go to module" forceMount>
                    {matchedModules.map((m) => (
                      <CommandItem key={`mm-${m.key}`} value={`module-${m.label}`} data-testid={`oc-module-${m.to.replace(/\//g, "-").replace(/^-/, "")}`}
                        onSelect={() => goTo(m.to)}>
                        <Compass className="h-4 w-4 text-[color:var(--st-accent)]" />
                        <span className="flex-1">{m.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {hasQuery && features.length > 0 && (
                  <CommandGroup heading="Features & pages">
                    {features.map((f) => (
                      <CommandItem key={`feat-${f.id}`} value={`feature-${f.name}-${f.id}`}
                        data-testid={`oc-feature-${f.id}`}
                        onSelect={() => openFeature(f)}>
                        <Compass className="h-4 w-4 text-sky-500" />
                        <span className="text-sm text-slate-800 truncate">{f.name}</span>
                        <span className="text-xs text-slate-400 truncate max-w-[220px] hidden sm:inline">{f.description}</span>
                        <span className="ml-auto text-[10px] text-slate-400">{f.space}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {hasQuery && ask && (
                  <CommandGroup heading={`Ask ONE — ${ask.interpretation}`}>
                    <CommandItem value={`ask-summary-${ask.interpretation}`}
                      data-testid="oc-ask-summary"
                      onSelect={() => goTo(ask.route_all)}>
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium text-slate-900">
                        {ask.count} record{ask.count === 1 ? "" : "s"}
                        {ask.total_value > 0 && <span className="text-slate-700 font-normal"> · {fmtINR(ask.total_value)}</span>}
                      </span>
                      <span className="ml-auto text-[11px] text-slate-400 inline-flex items-center gap-1">
                        View all <ArrowRight className="h-3 w-3" />
                      </span>
                    </CommandItem>
                    {ask.items.map((item) => (
                      <CommandItem key={`ask-${item.id}`} value={`ask-${item.type}-${item.id}`}
                        data-testid={`oc-ask-item-${item.id}`}
                        onSelect={() => openPreview(item)}>
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-800 truncate">{item.title}</span>
                        {item.subtitle && <span className="text-xs text-slate-400 truncate max-w-[160px]">{item.subtitle}</span>}
                        <span className="ml-auto flex items-center gap-2 shrink-0">
                          {item.value != null && <span className="font-mono text-xs text-slate-500">{fmtINR(item.value)}</span>}
                          {item.status && <StatusBadge value={item.status} />}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {hasQuery && fuzzy && groups.length > 0 && (
                  <div className="mx-2 mb-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid="oc-fuzzy-note">
                    Showing close matches — no exact results for “{q.trim()}”.
                  </div>
                )}
                {hasQuery && groups.map((g) => (
                  <CommandGroup key={g.key} heading={`${g.label} (${g.items.length})`}>
                    {g.items.map((item) => (
                      <ResultRow key={`${item.type}-${item.id}`} item={item}
                        onSelect={() => canPreview(item) ? openPreview(item) : (recordRecent(item), goTo(item.route))}
                        onOpenDirect={() => { recordRecent(item); goTo(item.route); }} />
                    ))}
                  </CommandGroup>
                ))}

                {!hasQuery && freq.length > 0 && (
                  <CommandGroup heading="Frequently used">
                    {freq.map((f) => (
                      <CommandItem key={`freq-${f.id}`} value={`frequent-${f.name}-${f.id}`}
                        data-testid={`oc-frequent-${f.id}`}
                        onSelect={() => openFeature(f)}>
                        <Sparkles className="h-4 w-4 text-sky-500" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-[10px] text-slate-400">{f.space}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasQuery && pins.length > 0 && (
                  <CommandGroup heading="Pinned">
                    {pins.map((p) => (
                      <CommandItem key={p.id} value={`pin-${p.title}-${p.entity_id}`}
                        data-testid={`oc-pin-${p.entity_id}`}
                        onSelect={() => openPreview({ type: p.entity_type, id: p.entity_id, title: p.title, subtitle: p.subtitle, route: p.route })}>
                        <Pin className="h-4 w-4 text-amber-500" />
                        <span className="flex-1 truncate">{p.title}</span>
                        {p.subtitle && <span className="text-xs text-slate-400 truncate max-w-[160px]">{p.subtitle}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasQuery && recent.length > 0 && (
                  <CommandGroup heading="Recent">
                    {recent.map((r) => (
                      <CommandItem key={r.id} value={`recent-${r.title}-${r.entity_id}`}
                        data-testid={`oc-recent-${r.entity_id}`}
                        onSelect={() => openPreview({ type: r.entity_type, id: r.entity_id, title: r.title, subtitle: r.subtitle, route: r.route })}>
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className="flex-1 truncate">{r.title}</span>
                        {r.subtitle && <span className="text-xs text-slate-400 truncate max-w-[160px]">{r.subtitle}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasQuery && (
                  <CommandGroup heading="Ask ONE">
                    {ASK_SUGGESTIONS.map((s, i) => (
                      <CommandItem key={s} value={`ask-suggestion-${s}`}
                        data-testid={`oc-ask-suggestion-${i}`}
                        onSelect={() => setQ(s)}>
                        <Sparkles className="h-4 w-4 text-amber-500" />
                        <span className="flex-1 text-slate-700">{s}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasQuery && (
                  <CommandGroup heading="Create">
                    {createActions.map((a) => (
                      <CommandItem key={a.key} value={a.label} data-testid={`oc-create-${a.key}`}
                        onSelect={() => goTo(a.to)}>
                        <Plus className="h-4 w-4 text-emerald-600" />
                        <span className="flex-1">{a.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {!hasQuery && (
                  <CommandGroup heading="Navigate">
                    {NAV_ACTIONS.map((a) => (
                      <CommandItem key={a.key} value={a.label} data-testid={`oc-nav-${a.key}`}
                        onSelect={() => goTo(a.to)}>
                        <Compass className="h-4 w-4 text-slate-400" />
                        <span className="flex-1">{a.label}</span>
                        {a.keys && <Kbd>{a.keys}</Kbd>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
            </div>
            {hasQuery && (
              <aside className="hidden lg:block w-[340px] shrink-0 border-l border-slate-100 bg-slate-50/60 max-h-[calc(55vh+49px)]" data-testid="oc-peek-pane">
                <SearchPeekCard item={activeItem}
                  canFullPreview={!!activeItem && canPreview(activeItem)}
                  onFullPreview={() => openPreview(activeItem)}
                  onOpen={(to) => { recordRecent(activeItem); goTo(to); }} />
              </aside>
            )}
           </div>
            <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 bg-slate-50">
              <span><Kbd>↵</Kbd> preview</span>
              <span><Kbd>↑↓</Kbd> navigate</span>
              <span><Kbd>esc</Kbd> close</span>
              {hasQuery && (
                <button type="button" data-testid="oc-pin-current-search"
                  onClick={() => { (store.isPinned(q) ? store.unpin(q.trim()) : store.pin(q.trim())); refocus(); }}
                  className="inline-flex items-center gap-1 hover:text-slate-800">
                  <Pin className={`h-3.5 w-3.5 ${store.isPinned(q) ? "text-amber-500" : ""}`} />
                  {store.isPinned(q) ? "Unpin search" : "Pin search"}
                </button>
              )}
              <button onClick={() => { setOpen(false); setHelpOpen(true); }}
                data-testid="oc-open-help"
                className="ml-auto inline-flex items-center gap-1 hover:text-slate-800">
                <Keyboard className="h-3.5 w-3.5" /> Shortcuts <Kbd>?</Kbd>
              </button>
              {tookMs != null && <span className="font-mono text-slate-400">{tookMs} ms</span>}
            </div>
          </div>
        </div>,
        document.body)
      )}

      {helpOpen && <ShortcutHelp onClose={() => setHelpOpen(false)} />}
      <QuickPreviewDrawer target={preview} onClose={() => setPreview(null)} />
    </>
  );
}

function ResultRow({ item, onSelect, onOpenDirect }) {
  const Icon = TYPE_ICON[item.type] || Search;
  return (
    <CommandItem value={`${item.type}-${item.id}`} onSelect={onSelect}
      data-testid={`oc-result-${item.type}-${item.id}`}>
      <Icon className="h-4 w-4 text-slate-400" />
      <span className="text-sm text-slate-800 truncate">{item.title}</span>
      {item.subtitle && <span className="hidden sm:inline text-xs text-slate-400 truncate max-w-[180px]">{item.subtitle}</span>}
      <span className="ml-auto flex items-center gap-2 shrink-0">
        {item.status && <StatusBadge value={item.status} />}
        <button onClick={(e) => { e.stopPropagation(); onOpenDirect(); }}
          data-testid={`oc-open-${item.type}-${item.id}`}
          title="Open full record"
          className="hidden sm:inline-block text-[10px] text-slate-500 hover:text-slate-900 bg-white/90 border border-slate-200 rounded px-1.5 py-0.5">
          open
        </button>
      </span>
    </CommandItem>
  );
}

const Kbd = ({ children }) => (
  <kbd className="font-mono text-[10px] text-slate-500 border border-slate-200 bg-white rounded px-1 py-0.5">{children}</kbd>
);

const SHORTCUTS = [
  ["⌘ / Ctrl + K", "Open ONE COMMAND — search, navigate, create"],
  ["↑ ↓ then ↵", "Move through results, open quick preview"],
  ["G then C", "Go to Customers"],
  ["G then Q", "Go to Quotations"],
  ["G then O", "Go to Orders"],
  ["G then P", "Go to Product Master"],
  ["G then D", "Go to Home"],
  ["?", "Toggle this shortcut help"],
  ["Esc", "Close palette / drawer"],
];

function ShortcutHelp({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    createPortal(
<div className="fixed inset-0 z-palette bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center px-4"
      onClick={onClose} data-testid="shortcut-help-overlay">
      <div className="bg-white w-full max-w-md rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Keyboard className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-900">Keyboard shortcuts</span>
          <Kbd>esc</Kbd>
        </div>
        <div className="p-4 space-y-2">
          {SHORTCUTS.map(([keys, desc]) => (
            <div key={keys} className="flex items-center gap-3 text-sm">
              <span className="w-32 shrink-0"><Kbd>{keys}</Kbd></span>
              <span className="text-slate-700">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
        document.body)
  );
}
